import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Alert, Box, Breadcrumbs, Button, Chip, Collapse, Divider, FormControl,
  InputLabel, MenuItem, Paper, Select, TextField, Typography,
} from '@mui/material';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import { format } from 'date-fns';
import AppShell from './components/AppShell';
import api from './services/api';
import { RangeSwitch, TrackerChart } from './quotas/trackerCharts';
import { snapshotsFromHistory } from './quotas/trackerSeries';

const STATUS_COLOURS = { active: 'success', stopped: 'default', expired: 'warning' };

const when = (value) => format(new Date(value), 'd MMM, HH:mm');

// Completes against the target per system: Alchemer from Ranger's snapshots, Wrangler from its
// on-demand history. The two are never added together.
const TrackerChartPanel = ({ surveyId, tracker }) => {
  const [series, setSeries] = useState({ alchemer: [], wrangler: [] });
  const [wranglerError, setWranglerError] = useState(null);
  const [range, setRange] = useState('all');

  useEffect(() => {
    api.get(`/quota-monitors/${tracker.quota_monitor}/snapshots/`)
      .then((res) => setSeries((prev) => ({ ...prev, alchemer: res.data })));
    api.get(`/surveys/${surveyId}/quota-trackers/${tracker.id}/wrangler-history/`)
      .then((res) => setSeries((prev) => ({ ...prev, wrangler: snapshotsFromHistory(res.data.wrangler) })))
      .catch((err) => setWranglerError(err?.response?.data?.error || "Couldn't load the Wrangler history."));
  }, [surveyId, tracker.id, tracker.quota_monitor]);

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <RangeSwitch value={range} onChange={setRange} />
      </Box>
      {wranglerError && <Alert severity="warning" sx={{ mb: 1 }}>Wrangler quotas are missing: {wranglerError}</Alert>}
      <TrackerChart series={series} rangeId={range} />
    </>
  );
};

const ExtendControl = ({ tracker, surveyId, onUpdate }) => {
  const [days, setDays] = useState(7);
  const [submitting, setSubmitting] = useState(false);

  const handleExtend = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post(`/surveys/${surveyId}/quota-trackers/${tracker.id}/extend/`, {
        extra_days: Number(days),
      });
      onUpdate(res.data);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleExtend} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <TextField
        aria-label="Days to add"
        type="number"
        size="small"
        required
        inputProps={{ min: 1, max: 365, style: { textAlign: 'right' } }}
        value={days}
        onChange={(e) => setDays(e.target.value)}
        sx={{ width: 72, '& input': { py: 0.6 } }}
      />
      <Button type="submit" variant="outlined" size="small" disabled={submitting} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
        Extend by {days || '…'} day{Number(days) === 1 ? '' : 's'}
      </Button>
    </Box>
  );
};

const TrackerCard = ({ tracker, name, surveyId, onUpdate, onStop }) => {
  const [chartOpen, setChartOpen] = useState(false);
  const snapshots = `${tracker.snapshot_count.toLocaleString()} Alchemer snapshot${tracker.snapshot_count !== 1 ? 's' : ''}`;
  const ended = tracker.status === 'stopped' && tracker.stopped_at ? ` · Stopped ${when(tracker.stopped_at)}` : '';
  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, px: 3, py: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" noWrap>{name}</Typography>
            <Chip label={tracker.status} color={STATUS_COLOURS[tracker.status]} size="small" />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Started {when(tracker.started_at)} · Expires {when(tracker.expires_at)}{ended} · {snapshots}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {tracker.status !== 'stopped' && <ExtendControl tracker={tracker} surveyId={surveyId} onUpdate={onUpdate} />}
          {tracker.status === 'active' && (
            <Button size="small" variant="outlined" color="error" onClick={onStop} sx={{ textTransform: 'none' }}>Stop</Button>
          )}
          <Button size="small" variant="text" onClick={() => setChartOpen(!chartOpen)} sx={{ textTransform: 'none' }}>
            {chartOpen ? 'Hide chart' : 'Show chart'}
          </Button>
          <Button size="small" variant="contained" component={Link} to={`/survey/${surveyId}/quota-tracker/${tracker.id}`}
            sx={{ textTransform: 'none' }}>
            Detail
          </Button>
        </Box>
      </Box>
      <Collapse in={chartOpen} unmountOnExit>
        <Divider sx={{ my: 2 }} />
        <TrackerChartPanel surveyId={surveyId} tracker={tracker} />
      </Collapse>
    </Paper>
  );
};

const QuotaTracker = () => {
  const { surveyId } = useParams();
  const [survey, setSurvey] = useState(null);
  const [trackers, setTrackers] = useState([]);
  const [quotaMonitors, setQuotaMonitors] = useState([]);
  const [form, setForm] = useState({ quota_monitor: '', max_days: 14 });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/surveys/${surveyId}/`).then((res) => setSurvey(res.data));
    api.get(`/quota-monitors/?survey=${surveyId}`).then((res) => setQuotaMonitors(res.data));
    api.get(`/surveys/${surveyId}/quota-trackers/`).then((res) => setTrackers(res.data));
  }, [surveyId]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.quota_monitor) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/surveys/${surveyId}/quota-trackers/`, {
        quota_monitor: form.quota_monitor,
        max_days: Number(form.max_days),
      });
      setTrackers((prev) => [res.data, ...prev]);
      setForm({ quota_monitor: '', max_days: 14 });
    } finally {
      setSubmitting(false);
    }
  };

  const handleStop = async (tracker) => {
    const res = await api.post(`/surveys/${surveyId}/quota-trackers/${tracker.id}/stop/`);
    setTrackers((prev) => prev.map((t) => (t.id === tracker.id ? res.data : t)));
  };

  const handleUpdate = (updated) => {
    setTrackers((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
  };

  if (!survey) return null;

  return (
    <AppShell maxWidth="lg">
      <Breadcrumbs separator=">" sx={{ mb: 4 }}>
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link>
        <Link to={`/project/${survey.project}`} style={{ textDecoration: 'none', color: 'inherit' }}>{survey.project_name}</Link>
        <Link to={`/survey/${surveyId}`} style={{ textDecoration: 'none', color: 'inherit' }}>{survey.name}</Link>
        <Typography>Quota Tracker</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
        <TimelineRoundedIcon color="secondary" />
        <Typography variant="h4" fontFamily="'Work Sans', sans-serif" fontWeight={700}>
          Quota Tracker
        </Typography>
      </Box>

      {/* Create form */}
      <Box sx={{ bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: 3, p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Set up a new tracker</Typography>
        {quotaMonitors.length === 0 ? (
          <Typography color="textSecondary">
            No quota views found for this survey.{' '}
            <Link to={`/survey/${surveyId}/quota-monitoring`}>Create one first.</Link>
          </Typography>
        ) : (
          <Box component="form" onSubmit={handleCreate}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <FormControl size="small" required sx={{ minWidth: 220 }}>
                <InputLabel>Quota view to track</InputLabel>
                <Select
                  value={form.quota_monitor}
                  label="Quota view to track"
                  onChange={(e) => setForm({ ...form, quota_monitor: e.target.value })}
                >
                  {quotaMonitors.map((qm) => (
                    <MenuItem key={qm.id} value={qm.id}>{qm.quota_view_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Max duration (days)"
                type="number"
                size="small"
                required
                inputProps={{ min: 1, max: 365 }}
                value={form.max_days}
                onChange={(e) => setForm({ ...form, max_days: e.target.value })}
                sx={{ width: 170 }}
              />
              <Button type="submit" variant="contained" disabled={submitting} sx={{ textTransform: 'none', borderRadius: 2 }}>
                Start
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* Existing trackers */}
      {trackers.length === 0 ? (
        <Typography color="textSecondary">No trackers yet.</Typography>
      ) : (
        trackers.map((tracker) => (
          <TrackerCard
            key={tracker.id}
            tracker={tracker}
            name={quotaMonitors.find((q) => q.id === tracker.quota_monitor)?.quota_view_name ?? `View #${tracker.quota_monitor}`}
            surveyId={surveyId}
            onUpdate={handleUpdate}
            onStop={() => handleStop(tracker)}
          />
        ))
      )}
    </AppShell>
  );
};

export default QuotaTracker;
